import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PENDING_MODULES, api } from '../../api/client';
import { AlertTriangle, FlaskConical } from 'lucide-react';

const SIDEBAR_KEY = 'mwm_sidebar_collapsed';

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const getPageTitle = (path: string): string => {
    switch (path) {
      case '/': return 'Fleet Overview & Platform Health';
      case '/customer-fleets': return 'Customer Fleet Health & Status';
      case '/cameras': return 'Platform Camera Registry';
      case '/camera-map': return 'Geographic Fleet Map';
      case '/offline-triage': return 'Offline-Reason Engine & Triage Board';
      case '/cloud-storage': return 'Cloud Storage Health & Recording Status';
      case '/sim-management': return '4G SIM & Cellular Telemetry';
      case '/alerts': return 'Fleet-Wide Alerts Stream';
      case '/customers': return 'Customer Administration & 360';
      case '/subscriptions': return 'Subscriptions & Entitlements';
      case '/payments': return 'Platform Payment Ledger';
      case '/plans': return 'Service Plan Catalogue';
      case '/inventory': return 'Hardware Inventory & Stock';
      case '/tickets': return 'Customer Support & Diagnostic Tickets';
      case '/admins': return 'Admin Staff & Role Management';
      case '/audit-log': return 'Immutable Audit Trail';
      case '/broadcast': return 'Customer Outage & Broadcast Announcements';
      case '/analytics': return 'Platform Revenue & Fleet Analytics';
      case '/security': return 'Account Security & Two-Factor Authentication';
      default: return 'Platform Console';
    }
  };

  const moduleKey = location.pathname.replace(/^\//, '');
  const pendingEndpoint = PENDING_MODULES[moduleKey];
  const isDemo = api.isDemo();

  return (
    <div className={`admin-layout${collapsed ? ' sidebar-collapsed' : ''}`}>
      <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
      <div className="admin-main">
        <Header title={getPageTitle(location.pathname)} />
        <main className="admin-content">
          {isDemo && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.4)',
                color: 'var(--text-main)', borderRadius: 'var(--radius-lg, 10px)',
                padding: '10px 16px', marginBottom: '16px', fontSize: '13px',
              }}
            >
              <FlaskConical size={16} />
              <span><strong>Demo mode</strong> — showing bundled sample data, not the live backend. Unset <code>mwm_demo</code> to use real data.</span>
            </div>
          )}
          {!isDemo && pendingEndpoint && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
                color: 'var(--text-main)', borderRadius: 'var(--radius-lg, 10px)',
                padding: '10px 16px', marginBottom: '16px', fontSize: '13px',
              }}
            >
              <AlertTriangle size={16} />
              <span>
                <strong>Backend endpoint not built yet.</strong> This module needs{' '}
                <code>{pendingEndpoint}</code>. It shows no data until that ships — nothing here is simulated.
              </span>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};
